import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { exhaustMap, from, of, switchMap, mergeMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { clubFeedActions } from './club-feed.actions';
import { ClubFeedService } from '../../services/club-feed.service';
import { ModalController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { activityPageActions } from '@fitup-monorepo/activity/lib/activity.page.actions';
import { clubActions } from '../club/club.actions';
import { ClubPickerModalComponent } from '../../components/club-picker-modal/club-picker-modal.component';
import { CreateClubFeedModalComponent } from '../../components/club-feed/create-club-feed-modal/create-club-feed-modal.component';

@Injectable()
export class ClubFeedEffects {
  private readonly actions$ = inject(Actions);
  private readonly clubFeedService = inject(ClubFeedService);
  private readonly modalController = inject(ModalController);
  private readonly store = inject(Store);

  public readonly loadClubFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.loadClubFeed, clubFeedActions.reloadClubFeed),
      switchMap(({ clubId }) =>
        this.clubFeedService.getLeagueChatMessages(clubId).pipe(
          map(messages => clubFeedActions.loadClubFeedSuccess({ clubId, messages })),
          catchError(error => of(clubFeedActions.loadClubFeedFailure({ clubId, error })))
        )
      )
    )
  );

  public readonly postMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.postMessage),
      switchMap(({ clubId, request }) =>
        this.clubFeedService.postMessage(clubId, request).pipe(
          switchMap(message => [
            clubFeedActions.postMessageSuccess({ clubId, message }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.postMessageFailure({ error })))
        )
      )
    )
  );

  public readonly updateMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.updateMessage),
      switchMap(({ clubId, messageId, request }) =>
        this.clubFeedService.updateMessage(clubId, messageId, request).pipe(
          switchMap(message => [
            clubFeedActions.updateMessageSuccess({ clubId, message }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.updateMessageFailure({ error })))
        )
      )
    )
  );

  public readonly deleteMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.deleteMessage),
      mergeMap(({ clubId, messageId }) =>
        this.clubFeedService.deleteMessage(clubId, messageId).pipe(
          switchMap(() => [
            clubFeedActions.deleteMessageSuccess({ clubId }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.deleteMessageFailure({ error })))
        )
      )
    )
  );

  public readonly likeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.likeMessage),
      mergeMap(({ clubId, messageId, icon }) =>
        this.clubFeedService.likeMessage(clubId, messageId, icon).pipe(
          switchMap(() => [
            clubFeedActions.likeMessageSuccess({ clubId }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.likeMessageFailure({ error })))
        )
      )
    )
  );

  public readonly unlikeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.unlikeMessage),
      mergeMap(({ clubId, messageId }) =>
        this.clubFeedService.unlikeMessage(clubId, messageId).pipe(
          switchMap(() => [
            clubFeedActions.unlikeMessageSuccess({ clubId }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.unlikeMessageFailure({ error })))
        )
      )
    )
  );

  public readonly commentMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.commentMessage),
      switchMap(({ clubId, messageId, request }) =>
        this.clubFeedService.commentMessage(clubId, messageId, request).pipe(
          switchMap(message => [
            clubFeedActions.commentMessageSuccess({ clubId, message }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.commentMessageFailure({ error })))
        )
      )
    )
  );

  public readonly updateComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.updateComment),
      switchMap(({ clubId, messageId, commentId, request }) =>
        this.clubFeedService.updateCommentMessage(clubId, messageId, commentId, request).pipe(
          switchMap(message => [
            clubFeedActions.updateCommentSuccess({ clubId, message }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.updateCommentFailure({ error })))
        )
      )
    )
  );

  public readonly deleteComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.deleteComment),
      mergeMap(({ clubId, messageId, commentId }) =>
        this.clubFeedService.deleteComment(clubId, messageId, commentId).pipe(
          switchMap(() => [
            clubFeedActions.deleteCommentSuccess({ clubId, commentId }),
            clubFeedActions.reloadClubFeed({ clubId }),
          ]),
          catchError(error => of(clubFeedActions.deleteCommentFailure({ error })))
        )
      )
    )
  );

  public readonly flagMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.flagMessage),
      mergeMap(({ clubId, messageId, request }) =>
        this.clubFeedService.flagMessage(clubId, messageId, request).pipe(
          map(() => clubFeedActions.flagMessageSuccess()),
          catchError(error => of(clubFeedActions.flagMessageFailure({ error })))
        )
      )
    )
  );

  public readonly flagComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(clubFeedActions.flagComment),
      mergeMap(({ clubId, messageId, commentId, request }) =>
        this.clubFeedService.flagComment(clubId, messageId, commentId, request).pipe(
          map(() => clubFeedActions.flagCommentSuccess()),
          catchError(error => of(clubFeedActions.flagCommentFailure({ error })))
        )
      )
    )
  );

  public readonly addToPost$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(activityPageActions.addToClubFeedRequested),
        exhaustMap(({ imageDataUrl }) => from(this.handleAddToPost(imageDataUrl)))
      ),
    { dispatch: false }
  );

  private async handleAddToPost(imageDataUrl: string): Promise<void> {
    this.store.dispatch(clubActions.loadClubOverview());
    const group = await ClubPickerModalComponent.openModal(this.modalController);
    if (!group) {
      return;
    }
    await CreateClubFeedModalComponent.openModal(this.modalController, group.id, undefined, imageDataUrl);
  }
}
